import moment from "moment";

import { toNumber } from "../DefineRolesAndShifts/helpers";

/** Sales contact for crews above the largest standard size. */
export const CREW_SIZE_SALES_EMAIL = "sales@yoram.com";

/** Events of up to this many operating days are charged per day ("Daily"). */
export const DAILY_EVENT_MAX_DAYS = 5;

export const EVENT_MODELS = {
  daily: "daily",
  extended: "extended",
};

export const fillTemplate = (template = "", values = {}) =>
  Object.keys(values).reduce(
    (text, key) => text.split(`{${key}}`).join(values[key]),
    template,
  );

export const formatAmount = (value) => Number(value || 0).toLocaleString("en-US");

/** Inclusive calendar days between event start and end (Step 1 dates). */
export const getOperatingDays = (startAt, endAt) => {
  if (!startAt || !endAt) return 0;
  const start = moment(startAt).locale("en").startOf("day");
  const end = moment(endAt).locale("en").startOf("day");
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 0;
  return end.diff(start, "days") + 1;
};

export const getEventModel = (operatingDays) =>
  operatingDays > DAILY_EVENT_MAX_DAYS
    ? EVENT_MODELS.extended
    : EVENT_MODELS.daily;

// ---- Crew size capacity (Roles & Shifts counter) ----

export const CAPACITY_STATES = {
  below: "below",
  at: "at",
  above: "above",
};

export const CAPACITY_FIELDS = [
  "maximum_number_of_males",
  "maximum_number_of_females",
];

/** Males + females entered across all roles. */
export const getTotalApplicants = (roles = []) =>
  roles.reduce(
    (total, role) =>
      total +
      CAPACITY_FIELDS.reduce((sum, field) => sum + toNumber(role?.[field]), 0),
    0,
  );

export const getCapacityState = (used, ceiling) => {
  if (used > ceiling) return CAPACITY_STATES.above;
  if (used === ceiling) return CAPACITY_STATES.at;
  return CAPACITY_STATES.below;
};

/**
 * Percentage above the ceiling, rounded up to a whole number like the backend's
 * `crew_size_exceeded` message (PROLINE-4015): 301 of 300 reads 1%, never 0%.
 */
export const getExceededPercent = (used, ceiling) =>
  ceiling > 0 && used > ceiling
    ? Math.ceil(((used - ceiling) / ceiling) * 100)
    : 0;

/** How many applicants are above the ceiling (0 when within it). */
export const getExceededBy = (roles, ceiling) =>
  Math.max(0, getTotalApplicants(roles) - ceiling);

/** Capacity left for one field before its own entry is counted. */
export const getRemainingForField = (roles, roleIndex, field, ceiling) => {
  const ownValue = toNumber(roles?.[roleIndex]?.[field]);
  return Math.max(0, ceiling - (getTotalApplicants(roles) - ownValue));
};

// ---- Custom plan (Super Admin, PROLINE-4057) ----

export const CUSTOM_CREW_SIZE_KEY = "custom";

/** The four standard crew sizes already cover up to 300 applicants. */
export const CUSTOM_CREW_SIZE_MIN = 301;

export const EMPTY_CUSTOM_CREW_PLAN = { applicants: "", rate: "" };

export const CUSTOM_PLAN_ERRORS = {
  required: "required",
  belowMinimum: "belowMinimum",
  invalidRate: "invalidRate",
};

/**
 * Validity of the Custom fields. Nothing is reported while both are empty;
 * a partly filled or invalid plan blocks "Save As Draft" and "Next Step".
 */
export const getCustomCrewPlanState = (plan) => {
  const applicants = String(plan?.applicants ?? "").trim();
  const rate = String(plan?.rate ?? "").trim();
  if (!applicants && !rate) {
    return { isEmpty: true, isValid: false, applicantsError: null, rateError: null };
  }

  const applicantsNumber = Number(applicants);
  let applicantsError = null;
  if (!applicants) {
    applicantsError = CUSTOM_PLAN_ERRORS.required;
  } else if (
    !Number.isInteger(applicantsNumber) ||
    applicantsNumber < CUSTOM_CREW_SIZE_MIN
  ) {
    applicantsError = CUSTOM_PLAN_ERRORS.belowMinimum;
  }

  let rateError = null;
  if (!rate) {
    rateError = CUSTOM_PLAN_ERRORS.required;
  } else if (!(Number(rate) > 0)) {
    rateError = CUSTOM_PLAN_ERRORS.invalidRate;
  }

  return {
    isEmpty: false,
    isValid: !applicantsError && !rateError,
    applicantsError,
    rateError,
  };
};

/**
 * Custom plan fee: Daily → rate per day x days; Extended → one flat event rate.
 * The custom rate is agreed for the whole crew, so it is not multiplied by the ceiling.
 */
export const calculateCustomCrewFee = ({
  rate,
  operatingDays,
  model = getEventModel(operatingDays),
}) => {
  return {
    model,
    rate,
    amount: model === EVENT_MODELS.extended ? rate : rate * operatingDays,
  };
};

// ---- Crew size plans from the API (PROLINE-4005) ----

export const PAYMENT_PERIODS = {
  daily: "daily",
  monthly: "monthly",
};

const toAmount = (value) =>
  value === null || value === undefined || value === "" ? null : Number(value);

/**
 * Billing follows the event's `payment_plan` (PROLINE-4075), else its `payment_period`
 * (daily → per day, monthly → one flat fee). Before either is known it falls back to
 * the operating days.
 */
export const getEventBillingModel = (event, operatingDays) => {
  const period = event?.payment_plan ?? event?.payment_period;
  if (period === PAYMENT_PERIODS.monthly) return EVENT_MODELS.extended;
  if (period === PAYMENT_PERIODS.daily) return EVENT_MODELS.daily;
  return getEventModel(operatingDays);
};

/**
 * One crew size card / selection, built from an API crew size plan
 * (`{ id, name, min_size, max_size, custom, daily_fee, monthly_fee, cost }`).
 * The custom plan has no sizes of its own: its ceiling and rate come from `customPlan`.
 */
export const toCrewSizeOption = (plan, customPlan) => {
  if (!plan) return null;
  if (plan.custom) {
    if (!getCustomCrewPlanState(customPlan).isValid) return null;
    return {
      id: plan.id,
      key: CUSTOM_CREW_SIZE_KEY,
      name: plan.name,
      custom: true,
      min: CUSTOM_CREW_SIZE_MIN,
      max: Number(customPlan.applicants),
      rate: Number(customPlan.rate),
      cost: toAmount(plan.cost),
    };
  }
  return {
    id: plan.id,
    key: String(plan.id),
    name: plan.name,
    custom: false,
    min: plan.min_size,
    max: plan.max_size,
    dailyFee: toAmount(plan.daily_fee),
    monthlyFee: toAmount(plan.monthly_fee),
    cost: toAmount(plan.cost),
  };
};

/**
 * Fee of a standard crew size as the backend prices it (PROLINE-4162): per day on a daily
 * event, flat on an extended one. The rate per organizer is not shown to the user.
 */
export const getCrewSizeFee = (option, model) =>
  (model === EVENT_MODELS.extended ? option?.monthlyFee : option?.dailyFee) ?? null;

/** Custom size and rate saved on the event, as the raw Custom field values. */
export const getEventCustomCrewPlan = (event) =>
  event?.custom_crew_size != null || event?.custom_rate != null
    ? {
        applicants: String(event.custom_crew_size ?? ""),
        rate: String(Number(event.custom_rate ?? "") || ""),
      }
    : EMPTY_CUSTOM_CREW_PLAN;

/** Crew size saved on the event (`event.crew_size_plan`), or null when none is set. */
export const getEventCrewSizeOption = (event) =>
  toCrewSizeOption(event?.crew_size_plan, getEventCustomCrewPlan(event));

/**
 * Subscription plan name in the current language. The plan is read from the stored login,
 * so its `name` keeps the language of the login; `name_en` / `name_ar` follow a switch.
 */
export const getSubscriptionPlanName = (plan, lang) =>
  plan?.[lang === "ar" ? "name_ar" : "name_en"] || plan?.name || "";

/** Rate of the subscription plan per organizer: per day on a daily event, flat on an extended one. */
export const getSubscriptionPlanRate = (plan, model) =>
  toAmount(model === EVENT_MODELS.extended ? plan?.extended_rate : plan?.daily_rate);
